import { api } from './client'

/** C-17 (REQ-238-001): registro unico dei lotti (sola lettura) e importazione prodotti. Dietro il flag `import.hub`. */

export type RegistryKind =
  | 'leads' | 'customers' | 'outcomes' | 'bank' | 'contracts_history' | 'numbers_payments' | 'products'
  | 'expenses' | 'expected_receivables' | 'historic_commissions'

export const REGISTRY_KIND_LABELS: Record<RegistryKind, string> = {
  leads: 'Lead',
  customers: 'Clienti',
  outcomes: 'Esiti operatore',
  bank: 'Movimenti bancari',
  contracts_history: 'Contratti storici',
  numbers_payments: 'Pagamenti NUMBERS',
  products: 'Prodotti',
  expenses: 'Spese',
  expected_receivables: 'Crediti attesi',
  historic_commissions: 'Provvigioni storiche',
}

export interface RegistryRow {
  kind: RegistryKind
  id: string
  source: string | null
  createdBy: number | null
  createdByName: string | null
  createdAt: string
  totalRows: number
  okRows: number
  problemRows: number
  status: string
  /** Lead e clienti si annullano dal tab "Storico lotti"; prodotti e tipi nuovi da qui (flag `import.hub_esteso`). */
  undoUrl: string | null
}

export interface ProductImportChange { field: string; from: string | null; to: string | null }

export interface ProductImportRowResult {
  line: number
  outcome: string
  problem: string | null
  name: string | null
  code: string | null
  /** R3-24: differenze rispetto al prodotto esistente (solo con l'aggiornamento per codice). */
  changes?: ProductImportChange[] | null
}

export interface ProductImportResult {
  dryRun: boolean
  batchId: string | null
  total: number
  created: number
  updated?: number
  existing: number
  invalid: number
  appliedMapping: Record<string, string>
  rows: ProductImportRowResult[]
}

export interface ProductImportField { key: string; label: string; required: boolean }

export interface HubImportType { kind: RegistryKind; label: string; columns: string[] }

export interface HubImportResult {
  dryRun: boolean
  kind: RegistryKind
  batchId: string | null
  total: number
  created: number
  existing: number
  invalid: number
  rows: { line: number; outcome: string; problem: string | null }[]
}

export interface UndoResult { batchId: string; deactivated?: number; restored?: number; undone?: number; skipped: number }

export const importHubApi = {
  registry: (kind?: RegistryKind) =>
    api<{ total: number; items: RegistryRow[] }>(`/api/import-registry${kind ? `?kind=${kind}` : ''}`),
  productFields: () => api<{ fields: ProductImportField[]; rule: string }>('/api/product-imports/fields'),
  productPreview: (csv: string, fileName: string | null, mapping: Record<string, string> | null, updateExisting = false) =>
    api<ProductImportResult>('/api/product-imports/preview', { method: 'POST', body: JSON.stringify({ csv, fileName, mapping, updateExisting }) }),
  productApply: (csv: string, fileName: string | null, mapping: Record<string, string> | null, updateExisting = false) =>
    api<ProductImportResult>('/api/product-imports/apply', { method: 'POST', body: JSON.stringify({ csv, fileName, mapping, updateExisting }) }),
  /** R3-24: annulla un lotto (prodotti o tipi nuovi) dall'`undoUrl` del registro. */
  undo: (undoUrl: string) => api<UndoResult>(undoUrl, { method: 'POST' }),
  hubTypes: () => api<HubImportType[]>('/api/import-hub/types'),
  hubPreview: (kind: RegistryKind, csv: string, fileName: string | null) =>
    api<HubImportResult>(`/api/import-hub/${kind}/preview`, { method: 'POST', body: JSON.stringify({ csv, fileName }) }),
  hubApply: (kind: RegistryKind, csv: string, fileName: string | null) =>
    api<HubImportResult>(`/api/import-hub/${kind}/apply`, { method: 'POST', body: JSON.stringify({ csv, fileName }) }),
}
